import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, timer } from 'rxjs';
import { BaseNotification, Notification, NotificationCssClass } from './notification';

@Injectable()
export class NotificationsService {

  private readonly addSubject = new Subject<Notification>();
  private readonly notificationsStateSubject = new BehaviorSubject<Notification[]>([]);
  private readonly removeSubject = new Subject<Notification>();

  /** the time in miliseconds before a notification automatically disappears */
  defaultDuration: number = 30000;

  /** observable to all add events */
  readonly add$ = this.addSubject.asObservable();

  /** observable to all changes to visible notifications */
  readonly notificationsState$ = this.notificationsStateSubject.asObservable();

  /** observable to all remove events */
  readonly remove$ = this.removeSubject.asObservable();

  /** tracks the ids assigned */
  private lastId = 0;

  /** internal map of notifications */
  private notificationMap = new Map<number, Notification>();

  /** currently displayed notifications */
  get notifications() {
    return Array.from(this.notificationMap.values()).sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  }

  /** Adds a notification */
  add(notification: BaseNotification): Notification {
    const systemId = ++this.lastId;
    const systemNotification = { ...notification, id: systemId };
    if (systemNotification.duration == null || systemNotification.duration <= 0) {
      systemNotification.duration = this.defaultDuration;
    }
    this.notificationMap.set(systemId, systemNotification);
    this.updateNotificationState();
    this.addSubject.next(systemNotification);
    timer(systemNotification.duration).subscribe(() => this.remove(systemId));
    return systemNotification;
  }

  /** Adds notification with danger css applied. */
  addDanger(message: string, duration?: number): Notification
  addDanger(message: string, heading?: string, duration?: number): Notification
  addDanger(message: string, headingOrDuration?: string | number, duration?: number): Notification {
    return this.addcommon(message, 'danger', headingOrDuration, duration);
  }

  /** Adds notification with dark css applied. */
  addDark(message: string, duration?: number): Notification
  addDark(message: string, heading?: string, duration?: number): Notification
  addDark(message: string, headingOrDuration?: string | number, duration?: number): Notification {
    return this.addcommon(message, 'dark', headingOrDuration, duration);
  }

  /**
   * Adds notification with danger css applied.  If an Error object is passed, the message property is used.
   * It is kind of stinky to use this.  Prefer addDanger.  If you have to pass an error, just use addDanger and set the mesage.
   */
  addError(error: Error | string, duration?: number): Notification {
    const message = typeof (error) === 'string' ? error : error.message;
    return this.addDanger(message, duration);
  }

  /** Adds notification with info css applied. */
  addInfo(message: string, duration?: number): Notification
  addInfo(message: string, heading?: string, duration?: number): Notification
  addInfo(message: string, headingOrDuration?: string | number, duration?: number): Notification {
    return this.addcommon(message, 'info', headingOrDuration, duration);
  }

  /** Adds notification with info light applied. */
  addLight(message: string, duration?: number): Notification
  addLight(message: string, heading?: string, duration?: number): Notification
  addLight(message: string, headingOrDuration?: string | number, duration?: number): Notification {
    return this.addcommon(message, 'light', headingOrDuration, duration);
  }

  /** Adds notification with info primary applied. */
  addPrimary(message: string, duration?: number): Notification
  addPrimary(message: string, heading?: string, duration?: number): Notification
  addPrimary(message: string, headingOrDuration?: string | number, duration?: number): Notification {
    return this.addcommon(message, 'primary', headingOrDuration, duration);
  }

  /** Adds notification with info secondary applied. */
  addSecondary(message: string, duration?: number): Notification
  addSecondary(message: string, heading?: string, duration?: number): Notification
  addSecondary(message: string, headingOrDuration?: string | number, duration?: number): Notification {
    return this.addcommon(message, 'secondary', headingOrDuration, duration);
  }

  /** Adds notification with success css applied. */
  addSuccess(message: string, duration?: number): Notification
  addSuccess(message: string, heading?: string, duration?: number): Notification
  addSuccess(message: string, headingOrDuration?: string | number, duration?: number): Notification {
    return this.addcommon(message, 'success', headingOrDuration, duration);
  }
  /** Adds notification with warning css applied. */
  addWarning(message: string, duration?: number): Notification
  addWarning(message: string, heading?: string, duration?: number): Notification
  addWarning(message: string, headingOrDuration?: string | number, duration?: number): Notification {
    return this.addcommon(message, 'warning', headingOrDuration, duration);
  }
  /** removes a notification if one exists with notificationId. */
  remove(notificationId: number): Notification | undefined {
    if (this.notificationMap.has(notificationId)) {
      const notification = this.notificationMap.get(notificationId);
      this.notificationMap.delete(notificationId);
      if (notification) {
        this.removeSubject.next(notification);
      }
      this.updateNotificationState();
      return notification;
    }
  }

  private addcommon(message: string, cssClass: NotificationCssClass, headingOrDuration?: string | number, duration?: number): Notification {
    let heading: string | undefined;
    switch (typeof headingOrDuration) {
      case 'string':
        heading = headingOrDuration;
        break;
      case 'number':
        if (duration == null) {
          duration = headingOrDuration;
        }
        break
    }
    return this.add({ cssClass, duration, heading, message });
  }

  private updateNotificationState() {
    const notifications = Array.from(this.notificationMap.values()).sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    this.notificationsStateSubject.next(notifications);
  }
}
